import type { LogData } from '../schemas/log/logSchema';

/**
 * The PostHog client calls the logger makes, typed by structure so the shared package depends on
 * neither PostHog SDK. Method syntax and optional `properties` match both SDKs' signatures.
 */
type LoggerPostHog = {
  capture(event: string, properties?: Record<string, unknown>): void;
  captureException(error: unknown, properties?: Record<string, unknown>): void;
};

/**
 * One way for every app to send logs to PostHog. Each app builds its `log` with this in its own
 * `logging.ts`. Rules: `posthog/README.md`. Levels:
 * - `info`: something normal happened that is worth seeing on a restaurant's timeline.
 * - `warn`: something went wrong but the app recovered, or an outside failure we expect (printer
 *   offline, network). No code needs fixing.
 * - `error`: a bug, or a failure the app didn't recover from, that someone must fix. Goes to Error
 *   tracking as a `$exception`.
 *
 * Every call takes a fixed `message` (no interpolated values) and typed `data` with its `area`.
 *
 * @param isTestMode - e2e runs: only print to the console, as `[LOG-<LEVEL>: <message>]` with the
 *   event's properties as the second argument, so specs can read what PostHog would receive.
 * @param isDev - Also print each log to the console, coloured by level.
 */
export const createLogger = ({
  posthog,
  isTestMode,
  isDev,
}: {
  posthog: LoggerPostHog;
  isTestMode: boolean;
  isDev: boolean;
}) => {
  const printDev = (text: string, style: string, properties: Record<string, unknown>) => {
    if (isDev) console.log(`%c ${text} \n`, style, JSON.stringify(properties, null, 3));
  };

  return {
    /** Sends a `log_info` event. */
    info: (message: string, data: LogData) => {
      const properties = { message, ...data };
      if (isTestMode) {
        console.log(`[LOG-INFO: ${message}]`, properties);
        return;
      }
      printDev(message, 'background: white; color: black', properties);
      posthog.capture('log_info', properties);
    },

    /** Sends a `log_warning` event. */
    warn: (message: string, data: LogData) => {
      const properties = { message, ...data };
      if (isTestMode) {
        console.warn(`[LOG-WARNING: ${message}]`, properties);
        return;
      }
      printDev(message, 'background: #FFFF00; color: black', properties);
      posthog.capture('log_warning', properties);
    },

    /**
     * Reports to Error tracking as a `$exception` event, at PostHog's `error` level.
     *
     * @param exception - The caught error. `undefined` when there is none: an `Error` with the
     *   message is reported instead, so it still shows up in Error tracking.
     */
    error: (message: string, exception: unknown, data: LogData) => {
      const properties = { message, ...data };
      if (isTestMode) {
        console.warn(`[LOG-ERROR: ${message}]`, properties);
        return;
      }
      printDev(
        exception === undefined ? message : `${message} ${String(exception)}`,
        'background: red; color: white',
        properties,
      );
      posthog.captureException(exception ?? new Error(message), properties);
    },
  };
};
export type Logger = ReturnType<typeof createLogger>;
